import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  JsonApiResponseDto,
  Response,
  TQueryParams,
  formatQueryParams,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';

export type TUserInitiativesType = {
  initiativeTypeId?: number;
  target: number;
  done: number;
  progressPercent: number;
  initiativeType: {
    id: number;
    name?: string;
    acronym?: string;
  };
};

export type TUserInitiativesProgress = {
  user: {
    id: number;
    name?: string;
    acronym?: string;
  };
  initiatives: TUserInitiativesType[];
  totals: {
    target: number;
    done: number;
    progressPercent: number;
  };
};

export type TInitiativesProgress = {
  id: string;
  period: {
    year: number;
    week?: number;
    untilWeek?: number;
    label: string;
  };
  users: TUserInitiativesProgress[];
  overall: {
    target: number;
    done: number;
    progressPercent: number;
  };
};

export type TInitiativeKpi = {
  id: string;
  date: Date;
  type: string;
  origin?: string;
  author?: {
    type: string;
    id?: string;
    name?: string;
  };
  data: any;
  createdAt: Date;
  updatedAt: Date;
};

export const analyticsApi = createApi({
  reducerPath: 'analytics',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_ANALYTICS_API_URL}/kpi`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Analytics'],
  endpoints: (builder) => ({
    getInitiativesProgress: builder.query<
      Response<TInitiativesProgress>,
      TQueryParams
    >({
      providesTags: ['Analytics'],
      query: (params) => formatQueryParams(params, 'initiatives-progress'),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TInitiativesProgress>(
          res as JsonApiResponseDto<TInitiativesProgress>,
          meta,
          args,
          true,
        );
      },
    }),
    getInitiativeKpis: builder.query<Response<TInitiativeKpi>, TQueryParams>({
      providesTags: ['Analytics'],
      query: (params) => formatQueryParams(params),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TInitiativeKpi>(
          res as JsonApiResponseDto<TInitiativeKpi>,
          meta,
          args,
          true,
        );
      },
    }),
    getAnadoluInitiativesProgress: builder.query<
      Response<TInitiativesProgress>,
      TQueryParams
    >({
      providesTags: ['Analytics'],
      query: (params) =>
        formatQueryParams(params, 'anadolu/initiatives-progress'),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TInitiativesProgress>(
          res as JsonApiResponseDto<TInitiativesProgress>,
          meta,
          args,
          true,
        );
      },
    }),
    getAnadoluInitiativeKpis: builder.query<
      Response<TInitiativeKpi>,
      TQueryParams
    >({
      providesTags: ['Analytics'],
      query: (params) => formatQueryParams(params, 'anadolu'),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TInitiativeKpi>(
          res as JsonApiResponseDto<TInitiativeKpi>,
          meta,
          args,
          true,
        );
      },
    }),
  }),
});

export const {
  useGetInitiativesProgressQuery,
  useGetInitiativeKpisQuery,
  useGetAnadoluInitiativesProgressQuery,
  useGetAnadoluInitiativeKpisQuery,
} = analyticsApi;
